import { useContext, useMemo, useState } from "react";
import { OutstandingCashItem } from "@common/contracts/reconciliation.contract";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { ReconciliationContext } from "./Reconciliation.context";
import {
    describeUnmatchedCashItems,
    getUnmatchedCashItems,
} from "./workflowHelpers";

interface Props {
    variant: "primary" | "secondary";
    outstandingCashItems: OutstandingCashItem[];
    onFinish: () => void;
    isFinishing: boolean;
    disabled: boolean;
    hotKey?: string;
}

const LABEL = "Finish reconciliation";

export const ReconciliationFinishButton: React.FC<Props> = ({
    variant,
    outstandingCashItems,
    onFinish,
    isFinishing,
    disabled,
    hotKey,
}) => {
    const { checkMerges } = useContext(ReconciliationContext);
    const [isConfirming, setIsConfirming] = useState(false);
    const unmatchedCashItems = useMemo(
        () =>
            getUnmatchedCashItems({
                cashItems: outstandingCashItems,
                checkMerges,
            }),
        [outstandingCashItems, checkMerges],
    );

    const requestFinish = () => {
        if (unmatchedCashItems.length > 0) {
            setIsConfirming(true);
            return;
        }
        onFinish();
    };
    const buttonProps = {
        variant,
        onClick: requestFinish,
        loading: isFinishing,
        disabled,
    };

    return (
        <>
            {hotKey ? (
                <ButtonWithLoader
                    {...buttonProps}
                    size="md"
                    hotKey={hotKey}
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{ description: LABEL }}
                >
                    {LABEL}
                </ButtonWithLoader>
            ) : (
                <ButtonWithLoader {...buttonProps} size="md">
                    {LABEL}
                </ButtonWithLoader>
            )}
            {isConfirming && (
                <ConfirmationModal
                    show
                    title="Finish reconciliation?"
                    question={describeUnmatchedCashItems(unmatchedCashItems)}
                    yes={LABEL}
                    no="Cancel"
                    onHide={() => setIsConfirming(false)}
                    onReject={() => setIsConfirming(false)}
                    onConfirm={() => {
                        setIsConfirming(false);
                        onFinish();
                    }}
                />
            )}
        </>
    );
};
